import { RUNTIME } from '../config/runtime';

/**
 * syncBrand
 * 
 * Sincroniza em tempo de execução os elementos de branding da página (HTML Document Head)
 * baseado no domínio ativo (APP vs MANAGER) e no esquema de cores atual (Dark vs Light).
 * 
 * - Evita flickering de interface e loops de renderização.
 * - Atualiza dinamicamente o document.title.
 * - Substitui o favicon dinamicamente de acordo com as especificações (Dark/Light).
 * - Chaveia o manifest.json da PWA para assegurar instalação individualizada.
 * - Atualiza o theme-color meta tag para integração elegante com a barra superior de dispositivos mobile.
 */
export const syncBrand = (isDarkMode: boolean): void => {
  if (typeof window === 'undefined') return;

  // 1. Título do Documento Dinâmico
  const expectedTitle = RUNTIME.appName;
  if (document.title !== expectedTitle) {
    document.title = expectedTitle;
  }

  // 2. Favicon Dinâmico baseados em domínio e Dark Mode
  // Regras homologadas pelo design:
  // - Light Mode Active -> /logo_light.png
  // - Dark Mode Active -> /logo_dark.png
  // Cadastro (abertura de conta WasteBank): ícone do WasteBank, igual nos dois temas
  const faviconUrl = RUNTIME.isCadastro ? '/wastebank_icon.png' : isDarkMode ? '/logo_dark.png' : '/logo_light.png';
  
  let faviconLink = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
  if (!faviconLink) {
    faviconLink = document.createElement('link');
    faviconLink.rel = 'shortcut icon';
    faviconLink.type = 'image/png';
    document.head.appendChild(faviconLink);
  }
  if (faviconLink.getAttribute('href') !== faviconUrl) {
    faviconLink.setAttribute('href', faviconUrl);
  }

  // Adiciona link alternativo clássico 'icon' se necessário para maior compatibilidade mobile
  let altFaviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
  if (!altFaviconLink) {
    altFaviconLink = document.createElement('link');
    altFaviconLink.rel = 'icon';
    altFaviconLink.type = 'image/png';
    document.head.appendChild(altFaviconLink);
  }
  if (altFaviconLink.getAttribute('href') !== faviconUrl) {
    altFaviconLink.setAttribute('href', faviconUrl);
  }

  // 3. Apple Touch Icon Dinâmico
  let appleTouchLink = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null;
  if (!appleTouchLink) {
    appleTouchLink = document.createElement('link');
    appleTouchLink.rel = 'apple-touch-icon';
    document.head.appendChild(appleTouchLink);
  }
  const appleTouchUrl = RUNTIME.isCadastro ? '/wastebank_apple_touch.png' : '/logo_light.png';
  if (appleTouchLink.getAttribute('href') !== appleTouchUrl) {
    appleTouchLink.setAttribute('href', appleTouchUrl);
  }

  // 4. Manifest PWA Dinâmico por domínio
  const manifestUrl = RUNTIME.isCadastro ? '/manifest_cadastro.json' : RUNTIME.isManager ? '/manifest_manager.json' : '/manifest.json';
  let manifestLink = document.querySelector("link[rel='manifest']") as HTMLLinkElement | null;
  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    document.head.appendChild(manifestLink);
  }
  if (manifestLink.getAttribute('href') !== manifestUrl) {
    manifestLink.setAttribute('href', manifestUrl);
  }

  // 5. Meta Theme-Color Dinâmico de acordo com runtime e dark-mode
  const themeColorMeta = RUNTIME.themeColor;
  const currentThemeColor = isDarkMode ? themeColorMeta.dark : themeColorMeta.light;
  let themeMeta = document.querySelector("meta[name='theme-color']") as HTMLMetaElement | null;
  if (!themeMeta) {
    themeMeta = document.createElement('meta');
    themeMeta.name = 'theme-color';
    document.head.appendChild(themeMeta);
  }
  if (themeMeta.getAttribute('content') !== currentThemeColor) {
    themeMeta.setAttribute('content', currentThemeColor);
  }
};
